import { useLayoutEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { styled } from "@pigment-css/react";
import { useShellChrome, useShellSlot } from "./ShellProvider";

const OverlayRegion = styled("section")({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  paddingBottom: "var(--bottom-nav-opaque-height, 0px)",
});

interface ShellOverlayProps {
  id: string;
  /** Accessible name of the region. */
  label: string;
  onDismiss: () => void;
  children: ReactNode;
}

/**
 * Renders `children` over the shell's content area and registers `onDismiss` with the shell, so
 * the shell's own dismiss gestures close it. Not modal: the top bar and bottom nav stay mounted
 * and interactive, nothing is scroll-locked, and focus is not managed.
 */
export function ShellOverlay({ id, label, onDismiss, children }: ShellOverlayProps) {
  const chrome = useShellChrome();
  const { ownerId, host } = useShellSlot(chrome, "overlay");

  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useLayoutEffect(() => chrome.openOverlay(id, () => dismissRef.current()), [chrome, id]);

  if (!host || ownerId !== id) return null;
  return createPortal(<OverlayRegion aria-label={label}>{children}</OverlayRegion>, host);
}
